import type { PayloadAction, ThunkAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import type { CurrentConnectorStatus } from '~/data/utils/connectorStatus'
import { CurrentConnectorStatuses } from '~/data/utils/connectorStatus'
import type { AppDispatch, RootState } from '~/store'
import { dispatcherFactory, selectorHookFactory } from '~/store/utils'

import {
  applyAddRow,
  applyChangeRowLayout,
  applyMoveOrSwap,
  applyRemoveRow,
  applyRemoveWidget,
  applySetWidget,
  applySetWidgetSettings,
  normalizeDashboardLayout
} from './customize/layoutHelpers'
import type { DashboardLayout, RowLayout, SlotSize, WidgetInstance } from './customize/types'

/**
 * ⚠️  CRITICAL: PERSISTED SLICE - READ BEFORE MAKING ANY CHANGES ⚠️
 *
 * This Redux slice is PERSISTED TO LocalStorage. Existing users have this state
 * stored in their browsers and it will be automatically loaded on page load.
 *
 * 🚨 BREAKING CHANGE RISKS:
 * - Adding required fields without backfilling defaults in `getCurrentUserState`
 *   will BREAK existing users when their persisted state is loaded
 * - Renaming existing fields will cause runtime errors for existing users
 * - Changing field types may cause type mismatches and crashes
 *
 * 📋 SAFE CHANGE PRACTICES:
 * ✅ When adding fields, supply backward-compatible defaults in `getCurrentUserState`
 *   (configuring a default in `initialState` DOES NOT WORK for persisted state)
 * ✅ Adding new optional properties to existing objects when handled defensively
 * ✅ Adding new reducer actions (these don't affect persisted state structure)
 * ✅ Modifying selector logic that handles missing fields gracefully
 *
 * 🚫 UNSAFE PRACTICES:
 * ❌ Adding required fields without defaults/migration in `getCurrentUserState`
 * ❌ Renaming or removing existing fields
 * ❌ Changing field types (string -> number, etc.)
 * ❌ Restructuring nested objects without backwards compatibility
 *
 * 💡 IF YOU MUST MAKE BREAKING CHANGES:
 * 1. Implement a migration strategy in the persistence layer
 * 2. Consider versioning the persisted state schema
 * 3. Test thoroughly with existing persisted state scenarios
 * 4. Plan a rollout strategy that handles users with old state
 *
 * Remember: This affects REAL USERS in production who may have months/years
 * of persisted state. One careless change can break the app for thousands of users.
 */

type SiteSelectState = {
  selectedSiteIds: string[]
  initialized?: boolean
}

type ConnectorStatusFilterStateValue = CurrentConnectorStatus[] | 'all'
type ConnectorSortStateValue = 'name' | 'power' | 'status' | 'rebootCount' | 'faultCount' | 'soc' | 'notes'
type SortDirection = 'asc' | 'desc'

type ConnectorOverviewControlsState = {
  connectorStatusFilter?: ConnectorStatusFilterStateValue
  connectorSort?: ConnectorSortStateValue
  connectorSortDir?: SortDirection
  favoritedConnectorIds?: string[]
}

type FailedChargesSelectedView = 'chargers' | 'vehicles'

type FailedChargesState = {
  selectedView?: FailedChargesSelectedView
}

type UserV2DashboardState = {
  siteSelect: SiteSelectState
  connectorOverviewControls: ConnectorOverviewControlsState
  failedCharges: FailedChargesState
}

type OldUserStateFormat = UserV2DashboardState
type NewUserStateFormat = {
  [orgId: string]: UserV2DashboardState
}

function isOldStateFormat(state: unknown): state is OldUserStateFormat {
  if (!state || typeof state !== 'object') return false
  const s = state as Record<string, unknown>
  return 'siteSelect' in s || 'connectorOverviewControls' in s
}

function getOrCreateNewFormatState(state: V2DashboardSlice, userId: string): NewUserStateFormat {
  const entry = state.userStates[userId]
  if (!entry) {
    const newFormat: NewUserStateFormat = {}
    state.userStates[userId] = newFormat
    return newFormat
  }
  if (isOldStateFormat(entry)) {
    const newFormat: NewUserStateFormat = {}
    state.userStates[userId] = newFormat
    return newFormat
  }
  return entry
}

type UserDashboardState = {
  activeLayoutId: string
  layouts: Record<string, DashboardLayout>
}

type V2DashboardSlice = {
  currentUser?: string
  userStates: {
    [key: string]: OldUserStateFormat | NewUserStateFormat
  }
  /**
   * Per-user dashboard layouts for the customizable dashboard. Optional and
   * lazy-seeded: users who have never edited their dashboard have no entry here,
   * and the customize hooks fall back to the registered template. Mutations
   * (move/swap/remove) trigger a one-time template clone before applying the change.
   */
  dashboards?: {
    [userId: string]: UserDashboardState
  }
}

const initialSiteSelectState: SiteSelectState = {
  selectedSiteIds: [],
  initialized: false
}

const initialConnectorOverviewControlsState: ConnectorOverviewControlsState = {
  connectorStatusFilter: 'all',
  connectorSort: 'name',
  connectorSortDir: 'asc',
  favoritedConnectorIds: []
}

const initialFailedChargesState: FailedChargesState = {
  selectedView: 'chargers'
}

const initialUserState: UserV2DashboardState = {
  siteSelect: { ...initialSiteSelectState },
  connectorOverviewControls: { ...initialConnectorOverviewControlsState },
  failedCharges: { ...initialFailedChargesState }
}

const initialState: V2DashboardSlice = {
  userStates: {}
}

const totalConnectorStatusCount = CurrentConnectorStatuses.length

function resolveConnectorStatusFilterValue(
  statuses?: ConnectorStatusFilterStateValue
): ConnectorStatusFilterStateValue {
  if (!statuses) return 'all'
  if (statuses === 'all' || statuses.length === totalConnectorStatusCount) return 'all'

  return statuses
}

// Read-only helper for selectors (doesn't mutate store)
function getCurrentUserStateReadonly(state: V2DashboardSlice, orgId: string): UserV2DashboardState {
  if (!state.currentUser) return { ...initialUserState }

  const userStateEntry = state.userStates[state.currentUser]

  if (!userStateEntry) return { ...initialUserState }

  if (isOldStateFormat(userStateEntry)) {
    const oldState = userStateEntry
    return {
      siteSelect: oldState.siteSelect ?? { ...initialSiteSelectState },
      connectorOverviewControls: oldState.connectorOverviewControls ?? { ...initialConnectorOverviewControlsState },
      failedCharges: { ...initialFailedChargesState }
    }
  }

  const newFormatState = userStateEntry as NewUserStateFormat
  const orgState = newFormatState[orgId]

  if (!orgState) return { ...initialUserState }

  const partialState: Partial<UserV2DashboardState> = orgState

  return {
    siteSelect: partialState.siteSelect ?? { ...initialSiteSelectState },
    connectorOverviewControls: partialState.connectorOverviewControls ?? { ...initialConnectorOverviewControlsState },
    failedCharges: partialState.failedCharges ?? { ...initialFailedChargesState }
  }
}

// Reducer helper: persists state and migrates old format if detected
function getCurrentUserState(state: V2DashboardSlice, orgId: string, principalOrgId?: string): UserV2DashboardState {
  if (!state.currentUser) return { ...initialUserState }

  const userStateEntry = state.userStates[state.currentUser]

  if (userStateEntry && isOldStateFormat(userStateEntry)) {
    const oldState = userStateEntry
    const migrationOrgId = principalOrgId || orgId

    if (migrationOrgId) {
      const newFormatState: NewUserStateFormat = {
        [migrationOrgId]: {
          siteSelect: oldState.siteSelect ?? { ...initialSiteSelectState },
          connectorOverviewControls: oldState.connectorOverviewControls ?? { ...initialConnectorOverviewControlsState },
          failedCharges: { ...initialFailedChargesState }
        }
      }
      state.userStates[state.currentUser] = newFormatState

      if (orgId && orgId !== migrationOrgId) {
        const newOrgState = { ...initialUserState }
        newFormatState[orgId] = newOrgState
        return newOrgState
      }

      return newFormatState[migrationOrgId]!
    }
    return {
      siteSelect: oldState.siteSelect ?? { ...initialSiteSelectState },
      connectorOverviewControls: oldState.connectorOverviewControls ?? { ...initialConnectorOverviewControlsState },
      failedCharges: { ...initialFailedChargesState }
    }
  }

  const newFormatState = getOrCreateNewFormatState(state, state.currentUser)
  if (!newFormatState[orgId]) {
    newFormatState[orgId] = { ...initialUserState }
  }

  return newFormatState[orgId]!
}

const v2DashboardSlice = createSlice({
  name: 'v2-dashboard',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<string>) => {
      state.currentUser = action.payload
      if (!state.userStates[action.payload]) {
        state.userStates[action.payload] = {}
      }
    },
    _setSelectedSiteIds: (
      state,
      action: PayloadAction<{ siteIds: string[]; orgId: string; principalOrgId?: string }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      userState.siteSelect.selectedSiteIds = [...action.payload.siteIds].sort()
      userState.siteSelect.initialized = true
    },
    _markSiteSelectInitialized: (state, action: PayloadAction<{ orgId: string; principalOrgId?: string }>) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      userState.siteSelect.initialized = true
    },
    _setConnectorStatusFilter: (
      state,
      action: PayloadAction<{
        filter: CurrentConnectorStatus[] | 'all'
        orgId: string
        principalOrgId?: string
      }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      const resolvedFilter =
        action.payload.filter === 'all' ? 'all' : resolveConnectorStatusFilterValue(action.payload.filter)
      userState.connectorOverviewControls.connectorStatusFilter = resolvedFilter
    },
    _ensureOrgState: (state, action: PayloadAction<{ orgId: string; principalOrgId?: string }>) => {
      getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
    },
    _setConnectorSort: (
      state,
      action: PayloadAction<{ sort: ConnectorSortStateValue; orgId: string; principalOrgId?: string }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      userState.connectorOverviewControls.connectorSort = action.payload.sort
    },
    _setConnectorSortDir: (
      state,
      action: PayloadAction<{ sortDir: SortDirection; orgId: string; principalOrgId?: string }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      userState.connectorOverviewControls.connectorSortDir = action.payload.sortDir
    },
    _addFavoritedConnectorId: (
      state,
      action: PayloadAction<{ connectorId: string; orgId: string; principalOrgId?: string }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      if (!userState.connectorOverviewControls.favoritedConnectorIds) {
        userState.connectorOverviewControls.favoritedConnectorIds = []
      }
      userState.connectorOverviewControls.favoritedConnectorIds.push(action.payload.connectorId)
    },
    _removeFavoritedConnectorId: (
      state,
      action: PayloadAction<{ connectorId: string; orgId: string; principalOrgId?: string }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      if (!userState.connectorOverviewControls.favoritedConnectorIds) {
        userState.connectorOverviewControls.favoritedConnectorIds = []
      }
      userState.connectorOverviewControls.favoritedConnectorIds =
        userState.connectorOverviewControls.favoritedConnectorIds.filter((id) => id !== action.payload.connectorId)
    },
    _setFailedChargesSelectedView: (
      state,
      action: PayloadAction<{ selectedView: FailedChargesSelectedView; orgId: string; principalOrgId?: string }>
    ) => {
      const userState = getCurrentUserState(state, action.payload.orgId, action.payload.principalOrgId)
      if (!userState.failedCharges) {
        userState.failedCharges = { ...initialFailedChargesState }
      }
      userState.failedCharges.selectedView = action.payload.selectedView
    },
    _seedDashboardLayout: (state, action: PayloadAction<{ userId: string; layout: DashboardLayout }>) => {
      const { userId, layout } = action.payload
      if (!state.dashboards) state.dashboards = {}
      let userDashboards = state.dashboards[userId]
      if (!userDashboards) {
        userDashboards = { activeLayoutId: layout.id, layouts: {} }
        state.dashboards[userId] = userDashboards
      }
      if (!userDashboards.layouts[layout.id]) {
        userDashboards.layouts[layout.id] = layout
      }
      userDashboards.activeLayoutId = layout.id
    },
    _moveOrSwapWidget: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; fromSlotId: string; toSlotId: string }>
    ) => {
      const { userId, layoutId, fromSlotId, toSlotId } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      userDashboards.layouts[layoutId] = applyMoveOrSwap(existing, fromSlotId, toSlotId)
    },
    _removeWidgetFromSlot: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; slotId: string }>
    ) => {
      const { userId, layoutId, slotId } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      userDashboards.layouts[layoutId] = applyRemoveWidget(existing, slotId)
    },
    _resetUserDashboards: (state, action: PayloadAction<{ userId: string }>) => {
      if (!state.dashboards) return
      delete state.dashboards[action.payload.userId]
    },
    _setWidgetInSlot: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; slotId: string; widget: WidgetInstance }>
    ) => {
      const { userId, layoutId, slotId, widget } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      userDashboards.layouts[layoutId] = applySetWidget(existing, slotId, widget)
    },
    _applyTemplate: (state, action: PayloadAction<{ userId: string; template: DashboardLayout }>) => {
      const { userId, template } = action.payload
      if (!state.dashboards) state.dashboards = {}
      state.dashboards[userId] = {
        activeLayoutId: template.id,
        layouts: { [template.id]: template }
      }
    },
    _changeRowLayout: (
      state,
      action: PayloadAction<{
        userId: string
        layoutId: string
        rowId: string
        nextLayout: RowLayout
        newSlotIds: string[]
        minSizeByWidgetId: Record<string, SlotSize | undefined>
      }>
    ) => {
      const { userId, layoutId, rowId, nextLayout, newSlotIds, minSizeByWidgetId } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      const { layout } = applyChangeRowLayout(
        normalizeDashboardLayout(existing),
        rowId,
        nextLayout,
        newSlotIds,
        (id) => minSizeByWidgetId[id]
      )
      userDashboards.layouts[layoutId] = layout
    },
    _addRow: (
      state,
      action: PayloadAction<{
        userId: string
        layoutId: string
        afterRowId: string | null
        newRowLayout: RowLayout
        newRowId: string
        newSlotIds: string[]
      }>
    ) => {
      const { userId, layoutId, afterRowId, newRowLayout, newRowId, newSlotIds } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      userDashboards.layouts[layoutId] = applyAddRow(existing, afterRowId, newRowLayout, newRowId, newSlotIds)
    },
    _removeRow: (state, action: PayloadAction<{ userId: string; layoutId: string; rowId: string }>) => {
      const { userId, layoutId, rowId } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      userDashboards.layouts[layoutId] = applyRemoveRow(existing, rowId)
    },
    _setWidgetSettings: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; slotId: string; settings: unknown }>
    ) => {
      const { userId, layoutId, slotId, settings } = action.payload
      const userDashboards = state.dashboards?.[userId]
      if (!userDashboards) return
      const existing = userDashboards.layouts[layoutId]
      if (!existing) return
      userDashboards.layouts[layoutId] = applySetWidgetSettings(existing, slotId, settings)
    }
  }
})

export default v2DashboardSlice.reducer

const {
  setCurrentUser,
  _setSelectedSiteIds,
  _markSiteSelectInitialized,
  _setConnectorStatusFilter,
  _ensureOrgState,
  _setConnectorSort,
  _setConnectorSortDir,
  _addFavoritedConnectorId,
  _removeFavoritedConnectorId,
  _setFailedChargesSelectedView,
  _seedDashboardLayout,
  _moveOrSwapWidget,
  _removeWidgetFromSlot,
  _resetUserDashboards,
  _setWidgetInSlot,
  _applyTemplate,
  _changeRowLayout,
  _addRow,
  _removeRow,
  _setWidgetSettings
} = v2DashboardSlice.actions

const updateV2SiteIds =
  (siteIds: string[]): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_setSelectedSiteIds({ siteIds, orgId: currentOrgId, principalOrgId }))
  }

const markSiteSelectInitialized =
  (): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> => (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_markSiteSelectInitialized({ orgId: currentOrgId, principalOrgId }))
  }

const ensureOrgState = (): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> => (dispatch, getState) => {
  const state = getState()
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const principalOrgId = state.currentOrganization.principalOrgId
  dispatch(_ensureOrgState({ orgId: currentOrgId, principalOrgId }))
}

const setConnectorStatusFilter =
  (filter: CurrentConnectorStatus[] | 'all'): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_setConnectorStatusFilter({ filter, orgId: currentOrgId, principalOrgId }))
  }

const setConnectorSort =
  (sort: ConnectorSortStateValue): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_setConnectorSort({ sort, orgId: currentOrgId, principalOrgId }))
  }

const setConnectorSortDir =
  (sortDir: SortDirection): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_setConnectorSortDir({ sortDir, orgId: currentOrgId, principalOrgId }))
  }

const addFavoritedConnectorId =
  (connectorId: string): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_addFavoritedConnectorId({ connectorId, orgId: currentOrgId, principalOrgId }))
  }

const removeFavoritedConnectorId =
  (connectorId: string): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_removeFavoritedConnectorId({ connectorId, orgId: currentOrgId, principalOrgId }))
  }

const setFailedChargesSelectedView =
  (selectedView: FailedChargesSelectedView): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    const state = getState()
    const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
    const principalOrgId = state.currentOrganization.principalOrgId
    dispatch(_setFailedChargesSelectedView({ selectedView, orgId: currentOrgId, principalOrgId }))
  }

const EMPTY_SITE_IDS: string[] = []
export const useV2SelectedSiteIds = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  const siteIds = userState.siteSelect?.selectedSiteIds
  return siteIds && siteIds.length > 0 ? siteIds : EMPTY_SITE_IDS
})

export const useSiteSelectInitialized = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  return userState.siteSelect?.initialized ?? false
})

export const useConnectorStatusFilter = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  return resolveConnectorStatusFilterValue(userState.connectorOverviewControls?.connectorStatusFilter)
})

export const useConnectorSort = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  return userState.connectorOverviewControls?.connectorSort ?? 'name'
})

export const useConnectorSortDir = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  return userState.connectorOverviewControls?.connectorSortDir ?? 'asc'
})

export const useFavoritedConnectorIds = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  return userState.connectorOverviewControls?.favoritedConnectorIds ?? []
})

export const useFailedChargesSelectedView = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const currentOrgId = state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
  const userState = getCurrentUserStateReadonly(sliceState, currentOrgId)
  return userState.failedCharges?.selectedView ?? 'chargers'
})

export const useSetCurrentUser = dispatcherFactory(setCurrentUser)

export const useUpdateV2SiteIds = () => {
  const dispatch = useDispatch<AppDispatch>()
  return (siteIds: string[]) => dispatch(updateV2SiteIds(siteIds))
}

export const useMarkSiteSelectInitialized = () => {
  const dispatch = useDispatch<AppDispatch>()
  return () => dispatch(markSiteSelectInitialized())
}

export const useEnsureOrgState = () => {
  const dispatch = useDispatch<AppDispatch>()
  return () => dispatch(ensureOrgState())
}

export const useSetConnectorStatusFilter = () => {
  const dispatch = useDispatch<AppDispatch>()
  // Stable identity across renders — important for `useMemo` dep arrays in callers
  // (e.g. `NewDashboard` registers page-shortcut handlers that close over this setter).
  return useCallback(
    (filter: CurrentConnectorStatus[] | 'all') => dispatch(setConnectorStatusFilter(filter)),
    [dispatch]
  )
}

export const useSetConnectorSort = () => {
  const dispatch = useDispatch<AppDispatch>()
  return (sort: ConnectorSortStateValue) => dispatch(setConnectorSort(sort))
}

export const useSetConnectorSortDir = () => {
  const dispatch = useDispatch<AppDispatch>()
  return (sortDir: SortDirection) => dispatch(setConnectorSortDir(sortDir))
}

export const useAddFavoritedConnectorId = () => {
  const dispatch = useDispatch<AppDispatch>()
  return (connectorId: string) => dispatch(addFavoritedConnectorId(connectorId))
}

export const useRemoveFavoritedConnectorId = () => {
  const dispatch = useDispatch<AppDispatch>()
  return (connectorId: string) => dispatch(removeFavoritedConnectorId(connectorId))
}

export const useSetFailedChargesSelectedView = () => {
  const dispatch = useDispatch<AppDispatch>()
  return (selectedView: FailedChargesSelectedView) => dispatch(setFailedChargesSelectedView(selectedView))
}

// =============================================================================
// Customizable-dashboard selectors and dispatch hooks
// =============================================================================

export const useCurrentDashboardUserId = selectorHookFactory((state) => state['v2-dashboard'].currentUser)

export const useUserDashboardState = selectorHookFactory((state) => {
  const sliceState = state['v2-dashboard']
  const userId = sliceState.currentUser
  if (!userId) return undefined
  return sliceState.dashboards?.[userId]
})

export const useSeedDashboardLayout = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layout: DashboardLayout) => dispatch(_seedDashboardLayout({ userId, layout })),
    [dispatch]
  )
}

export const useDispatchMoveOrSwapWidget = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, fromSlotId: string, toSlotId: string) =>
      dispatch(_moveOrSwapWidget({ userId, layoutId, fromSlotId, toSlotId })),
    [dispatch]
  )
}

export const useDispatchRemoveWidgetFromSlot = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, slotId: string) =>
      dispatch(_removeWidgetFromSlot({ userId, layoutId, slotId })),
    [dispatch]
  )
}

export const useDispatchResetUserDashboards = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((userId: string) => dispatch(_resetUserDashboards({ userId })), [dispatch])
}

export const useDispatchSetWidgetInSlot = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, slotId: string, widget: WidgetInstance) =>
      dispatch(_setWidgetInSlot({ userId, layoutId, slotId, widget })),
    [dispatch]
  )
}

export const useDispatchApplyTemplate = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, template: DashboardLayout) => dispatch(_applyTemplate({ userId, template })),
    [dispatch]
  )
}

export const useDispatchChangeRowLayout = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (
      userId: string,
      layoutId: string,
      rowId: string,
      nextLayout: RowLayout,
      newSlotIds: string[],
      minSizeByWidgetId: Record<string, SlotSize | undefined>
    ) =>
      dispatch(_changeRowLayout({ userId, layoutId, rowId, nextLayout, newSlotIds, minSizeByWidgetId })),
    [dispatch]
  )
}

export const useDispatchAddRow = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (
      userId: string,
      layoutId: string,
      afterRowId: string | null,
      newRowLayout: RowLayout,
      newRowId: string,
      newSlotIds: string[]
    ) => dispatch(_addRow({ userId, layoutId, afterRowId, newRowLayout, newRowId, newSlotIds })),
    [dispatch]
  )
}

export const useDispatchRemoveRow = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, rowId: string) => dispatch(_removeRow({ userId, layoutId, rowId })),
    [dispatch]
  )
}

export const useDispatchSetWidgetSettings = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, slotId: string, settings: unknown) =>
      dispatch(_setWidgetSettings({ userId, layoutId, slotId, settings })),
    [dispatch]
  )
}
